import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
    B2B_PACKAGE_TYPE,
    C2C_PACKAGE_TYPE,
    DOCUMENTS_PACKAGE_TYPE,
    STANDARD_PACKAGE_TYPE,
    VISIT_OFFICE_DELIVERY_TYPE,
    deliverySpeeds,
    paymentTypes,
    senderCities,
} from "../../../data/static-data";

import style from "../../../sass/pages/shipment/shipment.module.scss";
import { validators } from "../../../validators/validators";
import {
    selectIsEditingMode,
    selectShipmentAccordions,
    setAccordions,
} from "../shipmentSlice";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { selectUser } from "../../user/userSlice";
import { useTranslation } from "react-i18next";
import { isB2BPackage } from "../../../helpers/packageTypesHelper/packageTypesHelper";
import SenderTownInput from "./SenderTownInput";
import SenderDistrictInput from "./SenderDistrictInput";

const AdditionalData = ({ shipment, state, setState, errors, setErrors }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const isEditing = useSelector(selectIsEditingMode);
    const accordionState = useSelector(selectShipmentAccordions);
    const user = useSelector(selectUser);

    const [senderTownID, setSenderTownID] = useState("");
    useEffect(() => {
        setState((prev) => ({
            ...prev,
            "sender-town-id": senderTownID,
        }));
    }, [senderTownID, setState]);

    const [senderDistrictInitialID, setSenderDistrictInitialID] = useState("")
    const [senderDistrictID, setSenderDistrictID] = useState("");

    useEffect(() => {
        setState((prev) => {
            let gettingPackageType = prev["getting-package-type"];
            if(prev["getting-package-type"] === "courier" && !senderDistrictID) {
                gettingPackageType = VISIT_OFFICE_DELIVERY_TYPE
            }
            
            return {
                ...prev,
                "sender-district-id": senderDistrictID,
                "getting-package-type": gettingPackageType,
            }
        });
    }, [senderDistrictID, setState]);

    useEffect(() => {
        if(!state["sender-district-id"]) {
            setSenderDistrictInitialID(state["sender-district-id"])
        }
    }, [state]);

    const selectedCityHasExpressPickup = useMemo(() => {
        const city = senderCities.find(
            (city) => city.value === state["sender-city-code"]
        );
        return city?.hasExpressPickup ?? false;
    }, [state["sender-city-code"]]);

    const setDeliverySpeed = (e) => {
        setState((prev) => ({
            ...prev,
            "delivery-speed": e.target.value,
        }));
    };

    const setPackageType = (e) => {
        if (e.target.value === B2B_PACKAGE_TYPE) {
            setState((prev) => ({
                ...prev,
                "delivery-speed": deliverySpeeds.standard.value,
                "B2B-order-type": "courier",
                "B2B-order-number": "",
            }));
        }
        setState((prev) => ({
            ...prev,
            "package-type": e.target.value,
        }));
    };

    const setPaymentType = (e) => {
        setState((prev) => ({
            ...prev,
            "payment-type": e.target.value,
        }));
    };

    const setGettginPacageType = (e) => {
        setState((prev) => ({
            ...prev,
            "getting-package-type": e.target.value,
        }));
    };

    const setSenderCity = (e) => {
        const cityCode = e.target.value;
        setState((prev) => ({
            ...prev,
            "sender-city-code": cityCode,
        }));
    };

    const setDeclaredPrice = (e) => {
        setState((prev) => ({
            ...prev,
            "declared-price": e.target.value,
        }));
    };

    const setDeclaredPriceCurrency = (e) => {
        setState((prev) => ({
            ...prev,
            "declared-price-currency": e.target.value,
        }));
    };

    const setB2BOrderNumber = (e) => {
        if (!e.target.value) {
            setState((prev) => ({
                ...prev,
                "B2B-order-number": "",
            }));
        }

        if (validators.ValidateNumber(e.target.value)) {
            return;
        }

        setState((prev) => ({
            ...prev,
            "B2B-order-number": +e.target.value,
        }));
    };

    const setB2BOrderType = (e) => {
        setState((prev) => ({
            ...prev,
            "B2B-order-type": e.target.value,
        }));
    };

    const setSenderAddress = (e) => {
        setState((prev) => ({
            ...prev,
            "sender-address": e.target.value,
        }));
    };

    useEffect(() => {
        setState((prev) => ({
            ...prev,
            "sender-city": +shipment["sender-city-code"],
        }));
    }, [setState, shipment]);

    const handleAccordionChange = () => {
        dispatch(
            setAccordions({
                receiverOpened: accordionState.receiverOpened,
                additionalOpened: !accordionState.additionalOpened,
            })
        );
    };

    return (
        <div className="flex flex-col gap-2 w-full md:w-6/12">
            <Accordion
                expanded={accordionState.additionalOpened}
                className={
                    accordionState.additionalOpened &&
                    accordionState.receiverOpened
                        ? "h-full"
                        : ""
                }
                onChange={handleAccordionChange}
            >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <span className="text-emerald-600 text-lg font-bold">
                        {t("shipment.shipment_data.additional_data.title")}
                    </span>
                </AccordionSummary>
                <AccordionDetails className="flex flex-col gap-3">
                    <div className="grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold">
                        <span className="col-span-8 xs:col-span-4 text-left">
                            {t(
                                "shipment.shipment_data.additional_data.package_type.name"
                            )}
                        </span>
                        <select
                            className={`${style.select} col-span-10 xs:col-span-8 font-normal`}
                            id="sender-region"
                            value={state["package-type"]}
                            onChange={setPackageType}
                            disabled={!isEditing}
                        >
                            <option
                                defaultValue={
                                    state["package-type"] ===
                                    DOCUMENTS_PACKAGE_TYPE
                                }
                                value={DOCUMENTS_PACKAGE_TYPE}
                            >
                                {t(
                                    "shipment.shipment_data.additional_data.package_type.values.documents"
                                )}
                            </option>
                            {user && user["user-type"] === "individual" && (
                                <>
                                    <option
                                        defaultValue={
                                            state["package-type"] ===
                                            STANDARD_PACKAGE_TYPE
                                        }
                                        value={STANDARD_PACKAGE_TYPE}
                                    >
                                        {t(
                                            "shipment.shipment_data.additional_data.package_type.values.standard"
                                        )}
                                    </option>
                                    <option
                                        defaultValue={
                                            state["package-type"] ===
                                            C2C_PACKAGE_TYPE
                                        }
                                        value={C2C_PACKAGE_TYPE}
                                    >
                                        {t(
                                            "shipment.shipment_data.additional_data.package_type.values.C2C"
                                        )}
                                    </option>
                                </>
                            )}
                            {user && user["user-type"] === "legal" && (
                                <option
                                    defaultValue={
                                        state["package-type"] ===
                                        B2B_PACKAGE_TYPE
                                    }
                                    value={B2B_PACKAGE_TYPE}
                                >
                                    {t(
                                        "shipment.shipment_data.additional_data.package_type.values.B2B"
                                    )}
                                </option>
                            )}
                        </select>
                    </div>
                    <div className="grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold">
                        <span className="col-span-8 xs:col-span-4 text-left">
                            {t(
                                "shipment.shipment_data.additional_data.payment_type.name"
                            )}
                        </span>
                        <select
                            className={`${style.select} col-span-10 xs:col-span-8 font-normal`}
                            id="sender-region"
                            value={state["payment-type"]}
                            onChange={setPaymentType}
                            disabled={!isEditing}
                        >
                            {paymentTypes.map((option, index) => (
                                <option
                                    key={index}
                                    defaultValue={
                                        state["payment-type"] === option.value
                                    }
                                    value={option.value}
                                >
                                    {option.text[i18n.language]}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold">
                        <span className="col-span-8 xs:col-span-4 text-left">
                            {t(
                                "shipment.shipment_data.additional_data.declared_price.name"
                            )}
                        </span>
                        <input
                            className={`${style.input} col-span-7 xs:col-span-6 sm:col-span-4 font-normal`}
                            id="declared-price"
                            value={state["declared-price"]}
                            onChange={setDeclaredPrice}
                            disabled={!isEditing}
                        />
                        <select
                            className={`${style.select} col-span-3 xs:col-span-2 sm:col-span-4`}
                            style={{ maxWidth: 200 }}
                            id="declared-price-currency"
                            disabled={!isEditing}
                            value={state["declared-price-currency"]}
                            onChange={(e) => setDeclaredPriceCurrency(e)}
                        >
                            <option
                                defaultValue={
                                    state["declared-price-currency"] === "TRY"
                                }
                                value={"TRY"}
                            >
                                TL
                            </option>
                            <option
                                defaultValue={
                                    state["declared-price-currency"] === "USD"
                                }
                                value={"USD"}
                            >
                                USD
                            </option>
                            <option
                                defaultValue={
                                    state["declared-price-currency"] === "EUR"
                                }
                                value={"EUR"}
                            >
                                EUR
                            </option>
                        </select>
                        <span className="col-span-12 text-left text-3xs text-emerald-600">
                            {t(
                                "shipment.shipment_data.additional_data.declared_price.info"
                            )}
                        </span>
                    </div>
                    <div className="grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold">
                        <span className="col-span-8 xs:col-span-4 text-left">
                            {t(
                                "shipment.shipment_data.additional_data.sender_city.name"
                            )}
                        </span>
                        <select
                            className={`${style.select} col-span-10 xs:col-span-8 font-normal`}
                            id="sender-region"
                            value={state["sender-city-code"]}
                            onInput={setSenderCity}
                            disabled={!isEditing}
                        >
                            {senderCities.map((option, index) => (
                                <option
                                    selected={
                                        state["sender-city-code"] ===
                                        option.value
                                    }
                                    key={index}
                                    value={option.value}
                                >
                                    {option.text}
                                </option>
                            ))}
                        </select>
                    </div>
                    <div className="grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold">
                        <SenderTownInput
                            state={state["sender-town-id"]}
                            setState={setSenderTownID}
                            senderCityCode={state["sender-city-code"]}
                            enabled={isEditing}
                        />
                    </div>
                    <div className="grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold">
                        <SenderDistrictInput
                            state={state["sender-district-id"]}
                            setState={setSenderDistrictID}
                            initialID={senderDistrictInitialID}
                            senderTownID={state["sender-town-id"]}
                            enabled={isEditing}
                        />
                    </div>
                    <div className="grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold">
                        <span className="col-span-8 xs:col-span-4 text-left">
                            {t(
                                "shipment.shipment_data.additional_data.getting_package_type.name"
                            )}
                        </span>
                        <select
                            className={`${style.select} col-span-10 xs:col-span-8 font-normal`}
                            id="sender-region"
                            value={state["delivery-type"]}
                            onChange={setGettginPacageType}
                            disabled={!isEditing}
                        >
                            <option
                                selected={
                                    state["getting-package-type"] ===
                                    "visit office"
                                }
                                value={"visit office"}
                            >
                                {t(
                                    "shipment.shipment_data.additional_data.getting_package_type.values.visit_office"
                                )}
                            </option>
                            <option
                                selected={
                                    state["getting-package-type"] ===
                                    "local cargo"
                                }
                                value={"local cargo"}
                            >
                                {t(
                                    "shipment.shipment_data.additional_data.getting_package_type.values.local_cargo"
                                )}
                            </option>
                            {state["sender-district-id"] && (
                                <option
                                    selected={
                                        state["getting-package-type"] === "courier"
                                    }
                                    value={"courier"}
                                >
                                    {t(
                                        "shipment.shipment_data.additional_data.getting_package_type.values.courier"
                                    )}
                                </option>
                            )}
                        </select>
                    </div>
                    {state["getting-package-type"] === "courier" && (
                        <div className="grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold">
                            <span className="col-span-8 xs:col-span-4 text-left">
                                {t(
                                    "shipment.shipment_data.additional_data.courier_speed.name"
                                )}
                            </span>
                            <select
                                className={`${style.select} col-span-10 xs:col-span-8 font-normal`}
                                id="sender-region"
                                value={state["delivery-speed"]}
                                onChange={setDeliverySpeed}
                                disabled={!isEditing}
                            >
                                <option
                                    defaultValue={
                                        state["delivery-speed"] === "standard"
                                    }
                                    value={"standard"}
                                >
                                    {t(
                                        "shipment.shipment_data.additional_data.courier_speed.values.standard"
                                    )}
                                </option>
                                {selectedCityHasExpressPickup && (
                                    <option
                                        defaultValue={
                                            state["delivery-speed"] ===
                                            "express"
                                        }
                                        value={"express"}
                                    >
                                        {t(
                                            "shipment.shipment_data.additional_data.courier_speed.values.express"
                                        )}
                                    </option>
                                )}
                            </select>
                        </div>
                    )}

                    {state["getting-package-type"] === "courier" && (
                        <div className="grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold">
                            <span className="col-span-8 xs:col-span-4 text-left">
                                {t(
                                    "shipment.shipment_data.additional_data.sender_address.name"
                                )}
                            </span>
    
                            <input
                                className={`${style.input} col-span-10 xs:col-span-8 font-normal`}
                                id="sender-address"
                                value={state["sender-address"]}
                                disabled={!isEditing}
                                onChange={setSenderAddress}
                            />
                        </div>
                    )}

                    {state && isB2BPackage(state["package-type"]) && (
                        <>
                            <div className="grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold">
                                <span className="col-span-8 xs:col-span-4 text-left">
                                    {t(
                                        "shipment.shipment_data.additional_data.B2B_order_type.name"
                                    )}
                                </span>
                                <select
                                    className={`${style.select} col-span-10 xs:col-span-8 font-normal`}
                                    id="B2B-order-type"
                                    value={state["B2B-order-type"]}
                                    onInput={setB2BOrderType}
                                    disabled={!isEditing}
                                >
                                    <option
                                        defaultValue={!state["B2B-order-type"]}
                                        value={""}
                                        hidden={true}
                                    >
                                        {"Courier"}
                                    </option>
                                    <option
                                        defaultValue={
                                            state["B2B-order-type"] ===
                                            "courier"
                                        }
                                        value={"courier"}
                                    >
                                        {"Courier"}
                                    </option>
                                    <option
                                        defaultValue={
                                            state["B2B-order-type"] === "ETGB"
                                        }
                                        value={"ETGB"}
                                    >
                                        {"ETGB"}
                                    </option>
                                </select>
                            </div>

                            <div className="grid grid-cols-10 xs:grid-cols-12 items-center text-2xs font-bold">
                                <span className="col-span-8 xs:col-span-4 text-left">
                                    {t(
                                        "shipment.shipment_data.additional_data.B2B_order_number.name"
                                    )}
                                </span>
                                <input
                                    className={`${style.input} col-span-10 xs:col-span-8 font-normal`}
                                    id="b2b-order-number"
                                    value={state["B2B-order-number"]}
                                    onChange={setB2BOrderNumber}
                                    disabled={!isEditing}
                                />
                            </div>
                        </>
                    )}
                </AccordionDetails>
            </Accordion>
        </div>
    );
};

export default AdditionalData;